import { Capacitor } from '@capacitor/core';
import { Box, styled, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingLayout } from '../../component/layout/LoadingLayout';
import { downloadFileNative } from '../../utils/fileConversion/nativeFileDownload';
import { useBikeleasingDetails } from './useBikeleasingDetails';
import { BikeleasingCalculatorFormType, useBikeleasingCalculatorUrl } from './useBikeleasingLinks';

interface MessageData {
  blob: Blob;
  filename: string;
}

const DE_FORM_TYPES: BikeleasingCalculatorFormType[] = ['employee', 'selfemployed', 'official'];
const AT_FORM_TYPES: BikeleasingCalculatorFormType[] = ['employee', 'selfemployed'];

const SegmentedToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.selected : theme.palette.grey[100],
  borderRadius: 8,
  padding: 4,
  gap: 4,
  '& .MuiToggleButtonGroup-grouped': {
    flex: 1,
    border: 0,
    borderRadius: '6px !important',
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.bikeleasing.text,
    '&.Mui-selected': {
      backgroundColor: theme.palette.bikeleasing.primary,
      color: theme.palette.bikeleasing.text,
    },
  },
}));

export const BikeleasingCalculator: React.FC = () => {
  const { t } = useTranslation('benefitModule');
  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? theme.palette.bikeleasing.secondary : theme.palette.background.paper;
  const { tenant } = useTenant();
  const bikeleasingBenefitDetails = useBikeleasingDetails();
  const [formType, setFormType] = useState<BikeleasingCalculatorFormType>('employee');
  const calcUrl = useBikeleasingCalculatorUrl({
    companyCode: bikeleasingBenefitDetails?.bikeleasingCondition.companyCode,
    countryCode: tenant?.countryCode,
    formType,
  });

  const formTypes = tenant?.countryCode === TenantCountryCodeEnum.At ? AT_FORM_TYPES : DE_FORM_TYPES;

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleMessage = (e: MessageEvent) => {
        const messageData: MessageData = e.data;
        void downloadFileNative(messageData.filename, messageData.blob, 'application/pdf');
      };
      window.addEventListener('message', handleMessage, false);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [bikeleasingBenefitDetails]);

  if (!calcUrl) {
    return <LoadingLayout />;
  }
  return (
    <Box sx={{ backgroundColor }} height="100%" display="flex" flexDirection="column">
      <Box
        sx={{
          paddingTop: 1,
          paddingX: '8px',
          paddingBottom: '4px',
          '@media screen and (min-width: 545px)': {
            paddingX: '32px',
          },
          '@media screen and (min-width: 600px)': {
            paddingX: '8px',
          },
        }}
      >
        <SegmentedToggleGroup
          value={formType}
          exclusive
          fullWidth
          size="small"
          onChange={(_, value: BikeleasingCalculatorFormType | null) => {
            if (value) {
              setFormType(value);
            }
          }}
        >
          {formTypes.map(type => (
            <ToggleButton key={type} value={type}>
              {t(`extensions:bikeleasing_v2_benefit.calculatorFormType.${type}`)}
            </ToggleButton>
          ))}
        </SegmentedToggleGroup>
      </Box>
      <iframe
        src={calcUrl}
        seamless
        style={{
          textAlign: 'center',
          outline: 'none',
          border: 'none',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};
